import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { Route as EncodersLayout } from './_layout';
import { Layout } from '@/client/layouts/layout';
import { useEncodersStore, type EncoderWithClient } from '@/client/store/encodersStore';
import { useEncoderWSSStore, type EncoderWSSRecord } from '@/client/store/encoderWSSStore';
import { useClientsStore } from '@/client/store/clientsStore';
import { useLiveStreamsStore } from '@/client/store/liveStreamsStore';
import type { WebSocketEventData, LiveStreamWithEventResponse } from '@shared/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Input } from '@/client/components/ui/input';
import { Switch } from '@/client/components/ui/switch';
import { Label } from '@/client/components/ui/label';
import { Button } from '@/client/components/ui/button';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import { RefreshCw, X } from 'lucide-react';
import { API_BASE_URL } from '@/client/config';
import { formatBitrate, formatFrameRate, formatResolution } from '@/client/lib/format';
import { parseString, parseBoolean, buildSearchParams } from '@/client/lib/searchParams';
import { usePersistedSearch } from '@/client/hooks/usePersistedSearch';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';
import { SortableHeader, LiveStatus, LastCommunication } from '@/client/components/table-utils';
import { ClientNameCell, EventIDCell, EventLinksCell } from '@/client/components/tables/cells';
import type { EncoderSampleData } from '@shared/types';

type EncodersSearch = {
  clientID?: string;
  search?: string;
  onlineOnly?: boolean;
};

/** Encoder data merged with real-time WSS data */
interface EncoderWithWSS extends EncoderWithClient {
  wss: EncoderWSSRecord | null;
  /** Parsed stream info from DB (fallback when no WSS data) */
  dbStreamInfo: EncoderSampleData | null;
  /** Current live event using this encoder (if any) - from API or WebSocket */
  liveEvent: WebSocketEventData | LiveStreamWithEventResponse | null;
}

/** Parse JSON streamInfo from DB, returns null on failure */
function parseDbStreamInfo(encoder: EncoderWithClient): EncoderSampleData | null {
  try {
    const jsonStr = encoder.streamInfo?.streamInfo;
    if (!jsonStr) return null;
    return JSON.parse(jsonStr) as EncoderSampleData;
  } catch {
    return null;
  }
}

/** Get stream info, preferring WSS data over DB data */
function getStreamInfo(encoder: EncoderWithWSS): EncoderSampleData | null {
  return encoder.wss?.streamInfo ?? encoder.dbStreamInfo;
}

export const Route = createRoute({
  getParentRoute: () => EncodersLayout,
  path: 'encoders',
  validateSearch: (search: Record<string, unknown>): EncodersSearch => ({
    clientID: parseString(search.clientID),
    search: parseString(search.search),
    onlineOnly: parseBoolean(search.onlineOnly, true), // default: true
  }),
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: EncodersComponent,
});

const columnHelper = createColumnHelper<EncoderWithWSS>();

const columns = [
  columnHelper.accessor('clientName', {
    header: ({ column }) => <SortableHeader column={column}>Client</SortableHeader>,
    cell: (info) => {
      const clientName = info.getValue();
      if (!clientName) return '-';
      return <ClientNameCell clientID={info.row.original.clientID} clientName={clientName} />;
    },
  }),
  columnHelper.accessor('simpleName', {
    header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
    cell: (info) => info.getValue(),
  }),
  columnHelper.display({
    id: 'liveStatus',
    header: 'Status',
    cell: ({ row }) => <LiveStatus wss={row.original.wss} dbStatus={row.original.status} />,
  }),
  columnHelper.display({
    id: 'liveEvent',
    header: 'Live Event',
    cell: ({ row }) => {
      const liveEvent = row.original.liveEvent;
      if (!liveEvent) return <span className="text-muted-foreground">-</span>;
      return <EventIDCell eventID={liveEvent.eventID} />;
    },
  }),
  columnHelper.display({
    id: 'resolution',
    header: 'Resolution',
    cell: ({ row }) => {
      const streamInfo = getStreamInfo(row.original);
      if (!streamInfo?.width || !streamInfo?.height) return <span className="text-muted-foreground">-</span>;
      return formatResolution(streamInfo.width, streamInfo.height);
    },
  }),
  columnHelper.display({
    id: 'bitrate',
    header: 'Bitrate',
    cell: ({ row }) => {
      const streamInfo = getStreamInfo(row.original);
      if (!streamInfo?.bitrate) return <span className="text-muted-foreground">-</span>;
      return formatBitrate(streamInfo.bitrate);
    },
  }),
  columnHelper.display({
    id: 'frameRate',
    header: 'FPS',
    cell: ({ row }) => {
      const streamInfo = getStreamInfo(row.original);
      if (!streamInfo?.real_frame_rate) return <span className="text-muted-foreground">-</span>;
      return formatFrameRate(streamInfo.real_frame_rate);
    },
  }),
  columnHelper.accessor('inputURI', {
    header: 'Input URI',
    cell: (info) => <span className="font-mono text-xs">{info.getValue()}</span>,
    enableSorting: false,
  }),
  columnHelper.accessor('lastCommunication', {
    header: ({ column }) => <SortableHeader column={column}>Last Comm</SortableHeader>,
    cell: (info) => <LastCommunication lastCommunication={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'eventLinks',
    header: 'Links',
    cell: ({ row }) => {
      const liveEvent = row.original.liveEvent;
      if (!liveEvent) return null;
      return (
        <EventLinksCell
          eventID={liveEvent.eventID}
          clientID={liveEvent.clientID}
        />
      );
    },
    size: 70,
  }),
];

function EncodersComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();
  const { clientID, search: searchFilter = '', onlineOnly: showOnlineOnly = true } = Route.useSearch();

  // Persist search params across navigation
  usePersistedSearch('/encoders', { clientID: undefined, search: undefined, onlineOnly: true });

  const { encoders, loading, error, cacheInfo, fetchEncoders, invalidateCache } = useEncodersStore();
  const wssEncoders = useEncoderWSSStore((state) => state.encoders);
  const { clients, fetchClients } = useClientsStore();
  const liveStreams = useLiveStreamsStore((state) => state.liveStreams);
  const liveEvents = useLiveStreamsStore((state) => state.liveEvents);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'clientName', desc: false }]);

  // Build encoder → live event lookup map
  // Use API data (liveStreams) as base, WebSocket data (liveEvents) takes precedence
  const encoderLiveEventMap = useMemo(() => {
    const map = new Map<string, WebSocketEventData | LiveStreamWithEventResponse>();
    // First, populate from API data
    for (const stream of liveStreams) {
      if (stream.encoderID) {
        map.set(stream.encoderID, stream);
      }
    }
    // Then, WebSocket data takes precedence (more real-time)
    for (const event of liveEvents) {
      if (event.assignedEncoder) {
        map.set(event.assignedEncoder, event);
      }
    }
    return map;
  }, [liveStreams, liveEvents]);

  // Update URL when search filter changes
  const setSearchFilter = (value: string) => {
    navigate({
      to: '/encoders',
      search: buildSearchParams({ clientID, search: value || undefined, onlineOnly: showOnlineOnly ? undefined : false }),
    });
  };

  // Update URL when online-only toggle changes
  const setShowOnlineOnly = (value: boolean) => {
    navigate({
      to: '/encoders',
      search: buildSearchParams({ clientID, search: searchFilter || undefined, onlineOnly: value ? undefined : false }),
    });
  };

  // Get client name for the filter badge
  const filteredClient = clientID ? clients.find(c => c.clientID === clientID) : null;

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = await getAccessTokenSilently();
        await Promise.all([
          fetchEncoders(token, logout),
          clients.length === 0 ? fetchClients(token, logout) : Promise.resolve(),
        ]);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    loadData();
  }, [getAccessTokenSilently, fetchEncoders, fetchClients, clients.length]);

  // Merge static encoder data with real-time WSS data and live event info
  const filteredEncoders: EncoderWithWSS[] = useMemo(() => {
    // Merge WSS data with encoders, parse DB streamInfo as fallback, add live event
    let merged: EncoderWithWSS[] = encoders.map((encoder) => ({
      ...encoder,
      wss: wssEncoders.get(encoder.encoderID) || null,
      dbStreamInfo: parseDbStreamInfo(encoder),
      liveEvent: encoderLiveEventMap.get(encoder.encoderID) || null,
    }));

    // Filter by clientID if provided
    if (clientID) {
      merged = merged.filter((encoder) => encoder.clientID === clientID);
    }

    // Filter by online status (check both DB status and WSS online)
    if (showOnlineOnly) {
      merged = merged.filter((encoder) => {
        // If we have WSS data, use it
        if (encoder.wss) return encoder.wss.online;
        // Fall back to DB status
        return encoder.status === 1;
      });
    }

    // Filter by search text
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      merged = merged.filter((encoder) =>
        encoder.simpleName.toLowerCase().includes(search) ||
        encoder.clientName.toLowerCase().includes(search) ||
        encoder.inputURI.toLowerCase().includes(search)
      );
    }

    return merged;
  }, [encoders, wssEncoders, encoderLiveEventMap, clientID, searchFilter, showOnlineOnly]);

  const table = useReactTable({
    data: filteredEncoders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  const handleRefresh = async () => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE_URL}/api/cache/invalidate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key: 'encoders:all' }),
      });
      invalidateCache();
      await fetchEncoders(token, logout, true);
    } catch (err) {
      console.error('Error refreshing encoders:', err);
    }
  };

  const clearClientFilter = () => {
    navigate({
      to: '/encoders',
      search: buildSearchParams({ search: searchFilter || undefined, onlineOnly: showOnlineOnly ? undefined : false }),
    });
  };

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h3 className="text-2xl font-semibold">Encoders</h3>
              {filteredClient && (
                <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm">
                  <span>{filteredClient.name}</span>
                  <button
                    onClick={clearClientFilter}
                    className="ml-1 hover:bg-accent rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            <CacheTimestamp cachedAt={cacheInfo?.cachedAt ?? null} />
          </div>

          {loading && <p>Loading encoders...</p>}

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          {!loading && !error && encoders.length === 0 && (
            <p>No encoders found.</p>
          )}

          {!loading && !error && encoders.length > 0 && (
            <>
              <div className="flex justify-end items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="online-only"
                    checked={showOnlineOnly}
                    onCheckedChange={setShowOnlineOnly}
                  />
                  <Label htmlFor="online-only" className="cursor-pointer">
                    Online Only
                  </Label>
                </div>
                <Input
                  placeholder="Filter by name, client, or URI..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-80"
                />
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Force Refresh
                </Button>
                <ResetFiltersButton
                  routePath="/encoders"
                  defaultValues={{ search: undefined, onlineOnly: true }}
                />
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        onClick={() => navigate({ to: `/encoders/${row.original.encoderID}` })}
                        className="cursor-pointer hover:bg-accent"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Showing {filteredEncoders.length} of {encoders.length} encoders
              </p>
            </>
          )}
        </section>
      </div>
    </Layout>
  );
}
